import React, { createContext, FC, useCallback, useContext, useEffect, useState } from "react";
import { useRouter } from "router";

import { useAnalytics } from "@/components/analytics/useAnalytics";
import { IJobRequestURLFilter, jobRequestURLFilterType } from "@/data/job-requests";
import { getSkillById } from "@/data/skills";
import { ISkillOption } from "@/views/JobRequests";

import { jobRequestListSearchParamsType } from "./types";

interface JobRequestsFiltersContextValue {
  filters: IJobRequestURLFilter;
  appliedFilters: IJobRequestURLFilter;
  applied: boolean;
  empty: boolean;
  savedSearchChanged: boolean;
  additionalFiltersCount: number;

  changeFilter: <K extends keyof IJobRequestURLFilter>(key: K, value: IJobRequestURLFilter[K]) => void;
  handleFilterChange: <K extends keyof IJobRequestURLFilter>(key: K) => (value: IJobRequestURLFilter[K]) => void;
  applyFilters: () => void;
  clearFilters: () => void;
}

export const JobRequestsFiltersContext = createContext<JobRequestsFiltersContextValue>(
  {} as JobRequestsFiltersContextValue,
);

export const useJobRequestsFilters = () => useContext(JobRequestsFiltersContext);

interface JobRequestsFiltersProviderProps {}

export const JobRequestsFiltersProvider: FC<JobRequestsFiltersProviderProps> = ({ children }) => {
  const { sendEvent } = useAnalytics();

  const { searchParams, replaceSearchParams } = useRouter({
    ensureSearchParams: jobRequestListSearchParamsType,
  });

  const [filters, setFilters] = useState<IJobRequestURLFilter>(searchParams.filtersConfig);
  const [savedSearchSignature, setSavedSearchSignature] = useState<string>();
  const [savedSearchChanged, setSavedSearchChanged] = useState(false);

  const updateSkills = async () => {
    if (filters.skillIds && filters.skillIds.length > 0) {
      if (filters.skillIds.at(0)?.label == "") {
        if (filters.skillIds.at(0)) {
          const areSkillsLoaded = filters.skills?.length === filters.skillIds?.length;

          const response = areSkillsLoaded
            ? filters.skills ?? []
            : await getSkillById(filters.skillIds.map(item => item.value));

          const skills: ISkillOption[] = [];
          filters.skillIds.forEach(item => {
            const tmp = response.filter(itm => item.value == itm.id)[0];
            const option: ISkillOption = {
              value: tmp.id,
              label: tmp.name,
            };
            skills.push(option);
          });
          filters.skillIds = skills;
        }
      }
    }
  };

  useEffect(() => {
    if (JSON.stringify(searchParams.filtersConfig) !== JSON.stringify(filters)) {
      setFilters(searchParams.filtersConfig);
    }
  }, [searchParams.filtersConfig]);

  useEffect(() => {
    if (savedSearchSignature && savedSearchSignature === filters.signature) {
      setSavedSearchChanged(true);
    } else {
      setSavedSearchChanged(false);
      setSavedSearchSignature(filters.signature);
    }
    updateSkills();
  }, [filters]);

  const changeFilter = useCallback(
    <K extends keyof IJobRequestURLFilter>(key: K, value: IJobRequestURLFilter[K]) => {
      setFilters(f => ({ ...f, [key]: value }));
    },
    [setFilters],
  );

  const handleFilterChange = useCallback(
    <K extends keyof IJobRequestURLFilter>(key: K) =>
      (value: IJobRequestURLFilter[K]) => {
        changeFilter(key, value);
      },
    [changeFilter],
  );

  const applyFilters = useCallback(() => {
    replaceSearchParams({ ...searchParams, page: 0, filtersConfig: filters });
    sendEvent({ type: "search", search_term: filters.query ?? "" });
  }, [filters, searchParams, replaceSearchParams]);

  const clearFilters = useCallback(() => {
    setFilters({});
    replaceSearchParams({ ...searchParams, page: 0, filtersConfig: {}, tab: "all-jobs" });
  }, [searchParams, replaceSearchParams]);

  const compareFilters = useCallback((f1: IJobRequestURLFilter, f2: IJobRequestURLFilter) => {
    return JSON.stringify(jobRequestURLFilterType(f1)) === JSON.stringify(jobRequestURLFilterType(f2));
  }, []);

  const additionalFiltersCount =
    (!filters.location?.city || filters.radius === undefined ? 0 : 1) +
    (filters.remote?.length ?? 0) +
    (filters?.dedicated ? 1 : 0);

  return (
    <JobRequestsFiltersContext.Provider
      value={{
        filters,
        appliedFilters: searchParams.filtersConfig,
        applied: compareFilters(searchParams.filtersConfig, filters),
        empty: compareFilters(filters, {}),
        savedSearchChanged,
        additionalFiltersCount,

        changeFilter,
        handleFilterChange,
        applyFilters,
        clearFilters,
      }}>
      {children}
    </JobRequestsFiltersContext.Provider>
  );
};
